import { defaultConfig, mergeConfig } from "../config/defaultConfig.js";
import { DocumentStore } from "../data/DocumentStore.js";
import { DataManager } from "../data/DataManager.js";
import { Dot } from "../data/models/Dot.js";
import { LineObject } from "../data/models/LineObject.js";
import { RectangleObject } from "../data/models/RectangleObject.js";
import { EventBus } from "./EventBus.js";
import { calculateObjectMeasurements } from "../geometry/measurements.js";
import { clampZoom, normalizeViewport, screenToWorld, validateUnitConfiguration, worldToScreen } from "../geometry/coordinates.js";
import { Exporter } from "../io/Exporter.js";
import { Importer } from "../io/Importer.js";
import { CanvasRenderer } from "../rendering/CanvasRenderer.js";
import { ToolRegistry } from "../tools/ToolRegistry.js";
import { PanZoomTool } from "../tools/PanZoomTool.js";
import { CutLineTool } from "../tools/shapes/CutLineTool.js";
import { DotTool } from "../tools/shapes/DotTool.js";
import { LineTool } from "../tools/shapes/LineTool.js";
import { RectangleTool } from "../tools/shapes/RectangleTool.js";
import { ToolOptionsPanel } from "../ui/ToolOptionsPanel.js";
import { Toolbar } from "../ui/Toolbar.js";

export class DrawingTool {
  constructor(options = {}) {
    this.config = mergeConfig(defaultConfig, options);
    validateUnitConfiguration(this.config);
    this.viewport = normalizeViewport(this.config.viewport);
    this.store = new DocumentStore();
    this.eventBus = new EventBus();
    this.dataManager = new DataManager({
      store: this.store,
      exporter: new Exporter(),
      importer: new Importer(),
      getCoordinateConfig: () => this.config.coordinates
    });
    this.renderer = new CanvasRenderer(this.config);
    this.toolRegistry = new ToolRegistry();
    this.container = null;
    this.toolbar = null;
    this.toolOptionsPanel = null;
    this.preview = null;
    this.drawingFocused = false;
    this.boundWheelHandler = (event) => this.handleWheel(event);
    this.boundKeyHandler = (event) => this.handleKeyDown(event);
    this.boundPointerDownHandler = (event) => this.handlePointerDown(event);
    this.boundPointerMoveHandler = (event) => this.handlePointerMove(event);
    this.boundPointerUpHandler = (event) => this.handlePointerUp(event);
    this.boundPointerCancelHandler = (event) => this.handlePointerCancel(event);
    this.boundDocumentPointerDownHandler = (event) => this.handleDocumentPointerDown(event);
    this.boundFocusInHandler = (event) => this.handleFocusIn(event);
    this.inputHandlersAttached = false;
    this.keyboardHandlerAttached = false;
    this.pointerHandlersAttached = false;
    this.focusHandlersAttached = false;

    this.registerDefaultTools();
    this.registerViewportCommands();
    this.toolRegistry.setActive(this.config.tools.defaultTool);
  }

  registerDefaultTools() {
    this.toolRegistry.register(new DotTool());
    this.toolRegistry.register(new LineTool());
    this.toolRegistry.register(new RectangleTool());
    this.toolRegistry.register(new CutLineTool());
    this.toolRegistry.register(new PanZoomTool());
  }

  mount(target) {
    const container = resolveContainer(target);
    this.detachToolInputHandlers();
    this.detachViewportInputHandlers();
    this.detachFocusHandlers();
    container.innerHTML = "";
    this.container = container;

    if (this.config.ui.showToolbar) {
      this.toolbar = new Toolbar({
        toolRegistry: this.toolRegistry,
        onSelect: (toolId) => this.setTool(toolId),
        enabledTools: this.config.tools.enabled
      });
      this.toolbar.mount(container);
    }

    if (this.config.ui.showToolOptions) {
      this.toolOptionsPanel = new ToolOptionsPanel({
        config: this.config,
        onChange: (toolOptions) => {
          this.setConfig({ toolOptions });
          this.setDrawingFocus(true);
        },
        onFocus: () => this.setDrawingFocus(false)
      });
      this.toolOptionsPanel.mount(container);
    }

    this.renderer.mount(container);
    this.attachToolInputHandlers();
    this.attachViewportInputHandlers();
    this.attachFocusHandlers();
    this.seedDemoElements();
    this.render();
    return this;
  }

  destroy() {
    this.detachToolInputHandlers();
    this.detachViewportInputHandlers();
    this.detachFocusHandlers();
    this.clearPreview();

    if (this.container) {
      this.container.innerHTML = "";
    }

    this.container = null;
    this.toolbar = null;
    this.toolOptionsPanel = null;
    this.setDrawingFocus(false);
  }

  setConfig(partialConfig = {}) {
    this.config = mergeConfig(this.config, partialConfig);
    validateUnitConfiguration(this.config);

    if (partialConfig.viewport) {
      this.viewport = normalizeViewport({
        ...this.viewport,
        ...partialConfig.viewport
      });
    }

    this.renderer.setConfig(this.config);
    this.refreshViewportInputHandlers();
    this.render();
    this.eventBus.emit("config:changed", { config: this.getConfig() });
    return this.getConfig();
  }

  getConfig() {
    return structuredClone(this.config);
  }

  getDocument() {
    return this.store.getDocument();
  }

  setDocument(documentModel) {
    this.store.setDocument(documentModel);
    this.render();
  }

  save() {
    return this.dataManager.save();
  }

  load(data) {
    this.dataManager.load(data);
    this.render();
  }

  export(format) {
    return this.dataManager.export(format);
  }

  import(format, payload, options = {}) {
    this.dataManager.import(format, payload, options);
    this.render();
  }

  setTool(toolId) {
    this.toolRegistry.getActive()?.cancel();
    this.clearPreview();
    const tool = this.toolRegistry.setActive(toolId);
    this.setDrawingFocus(true);
    this.eventBus.emit("tool:changed", { toolId });
    return tool;
  }

  on(eventName, listener) {
    return this.eventBus.on(eventName, listener);
  }

  off(eventName, listener) {
    this.eventBus.off(eventName, listener);
  }

  emit(eventName, payload) {
    this.eventBus.emit(eventName, payload);
    return this;
  }

  getViewport() {
    return structuredClone(this.viewport);
  }

  getPreview() {
    return this.preview ? structuredClone(this.preview) : null;
  }

  setPreview(preview) {
    this.preview = structuredClone(preview);
    this.render();
    return this.getPreview();
  }

  clearPreview() {
    if (!this.preview) {
      return null;
    }

    this.preview = null;
    this.render();
    return null;
  }

  hasDrawingFocus() {
    return this.drawingFocused;
  }

  setDrawingFocus(isFocused) {
    const nextFocus = Boolean(isFocused);

    if (this.drawingFocused === nextFocus) {
      return this.drawingFocused;
    }

    this.drawingFocused = nextFocus;
    this.eventBus.emit("drawing-focus:changed", { focused: this.drawingFocused });
    return this.drawingFocused;
  }

  setViewport(viewportPatch = {}) {
    const previousViewport = this.getViewport();
    const nextViewport = normalizeViewport({
      ...this.viewport,
      ...viewportPatch
    });

    if (areViewportsEqual(previousViewport, nextViewport)) {
      return this.getViewport();
    }

    this.viewport = nextViewport;
    this.render();
    this.eventBus.emit("viewport:changed", {
      viewport: this.getViewport(),
      previousViewport
    });

    return this.getViewport();
  }

  panBy({ x = 0, y = 0 } = {}) {
    this.setDrawingFocus(true);
    return this.setViewport({
      panX: this.viewport.panX + Number(x),
      panY: this.viewport.panY + Number(y)
    });
  }

  panTo({ x = this.viewport.panX, y = this.viewport.panY } = {}) {
    this.setDrawingFocus(true);
    return this.setViewport({
      panX: Number(x),
      panY: Number(y)
    });
  }

  zoomBy(factor, anchor) {
    this.setDrawingFocus(true);
    return this.zoomTo(this.viewport.zoom * Number(factor), anchor);
  }

  zoomTo(zoom, anchor = this.getViewportCenter()) {
    this.setDrawingFocus(true);
    const nextZoom = clampZoom(zoom, this.viewport.minZoom, this.viewport.maxZoom);
    const anchorPoint = anchor ?? this.getViewportCenter();
    const worldAnchor = screenToWorld(anchorPoint, this.viewport);

    return this.setViewport({
      zoom: nextZoom,
      panX: Number(anchorPoint.x) - worldAnchor.x * nextZoom,
      panY: Number(anchorPoint.y) - worldAnchor.y * nextZoom
    });
  }

  resetViewport() {
    this.setDrawingFocus(true);
    return this.setViewport(this.config.viewport);
  }

  getObjectMeasurements(objectId, options = {}) {
    const documentModel = this.store.getDocument();
    const object = documentModel.objects.find((candidate) => candidate.id === objectId);

    if (!object) {
      throw new Error(`Unknown object: ${objectId}`);
    }

    const dotsById = new Map(documentModel.dots.map((dot) => [dot.id, dot]));

    return calculateObjectMeasurements(object, dotsById, {
      coordinates: this.config.coordinates,
      displayUnit: options.displayUnit ?? this.config.measurements.displayUnit,
      precision: options.precision ?? this.config.measurements.precision
    }).map(({ start, end, ...measurement }) => measurement);
  }

  render() {
    this.renderer.render(this.store.getDocument(), {
      viewport: this.viewport,
      coordinates: this.config.coordinates,
      grid: this.config.grid,
      measurements: this.config.measurements,
      preview: this.preview
    });
  }

  registerViewportCommands() {
    this.eventBus.on("viewport:pan-by", (payload) => this.panBy(payload));
    this.eventBus.on("viewport:pan-to", (payload) => this.panTo(payload));
    this.eventBus.on("viewport:zoom-by", (payload = {}) => this.zoomBy(payload.factor ?? payload.zoom ?? 1, payload.anchor));
    this.eventBus.on("viewport:zoom-to", (payload = {}) => this.zoomTo(payload.zoom, payload.anchor));
    this.eventBus.on("viewport:reset", () => this.resetViewport());
  }

  attachViewportInputHandlers() {
    const canvas = this.renderer.canvas;

    if (canvas && this.config.viewport.wheelZoom) {
      canvas.addEventListener("wheel", this.boundWheelHandler, { passive: false });
      this.inputHandlersAttached = true;
    }

    if (this.config.viewport.keyboard) {
      document.addEventListener("keydown", this.boundKeyHandler);
      this.keyboardHandlerAttached = true;
    }
  }

  detachViewportInputHandlers() {
    const canvas = this.renderer.canvas;

    if (canvas && this.inputHandlersAttached) {
      canvas.removeEventListener("wheel", this.boundWheelHandler);
      this.inputHandlersAttached = false;
    }

    if (this.keyboardHandlerAttached) {
      document.removeEventListener("keydown", this.boundKeyHandler);
      this.keyboardHandlerAttached = false;
    }
  }

  refreshViewportInputHandlers() {
    if (!this.container) {
      return;
    }

    this.detachViewportInputHandlers();
    this.attachViewportInputHandlers();
  }

  attachFocusHandlers() {
    if (typeof document === "undefined" || this.focusHandlersAttached) {
      return;
    }

    document.addEventListener("pointerdown", this.boundDocumentPointerDownHandler);
    document.addEventListener("focusin", this.boundFocusInHandler);
    this.focusHandlersAttached = true;
  }

  detachFocusHandlers() {
    if (typeof document === "undefined" || !this.focusHandlersAttached) {
      return;
    }

    document.removeEventListener("pointerdown", this.boundDocumentPointerDownHandler);
    document.removeEventListener("focusin", this.boundFocusInHandler);
    this.focusHandlersAttached = false;
  }

  handleDocumentPointerDown(event) {
    if (!this.container || !event.target) {
      this.setDrawingFocus(false);
      return;
    }

    if (!this.container.contains(event.target)) {
      this.setDrawingFocus(false);
      return;
    }

    this.setDrawingFocus(!isEditableElement(event.target));
  }

  handleFocusIn(event) {
    if (!this.container || !event.target) {
      this.setDrawingFocus(false);
      return;
    }

    if (!this.container.contains(event.target)) {
      this.setDrawingFocus(false);
      return;
    }

    this.setDrawingFocus(!isEditableElement(event.target));
  }

  attachToolInputHandlers() {
    const canvas = this.renderer.canvas;

    if (!canvas || this.pointerHandlersAttached) {
      return;
    }

    canvas.addEventListener("pointerdown", this.boundPointerDownHandler);
    canvas.addEventListener("pointermove", this.boundPointerMoveHandler);
    canvas.addEventListener("pointerup", this.boundPointerUpHandler);
    canvas.addEventListener("pointercancel", this.boundPointerCancelHandler);
    this.pointerHandlersAttached = true;
  }

  detachToolInputHandlers() {
    const canvas = this.renderer.canvas;

    if (!canvas || !this.pointerHandlersAttached) {
      return;
    }

    canvas.removeEventListener("pointerdown", this.boundPointerDownHandler);
    canvas.removeEventListener("pointermove", this.boundPointerMoveHandler);
    canvas.removeEventListener("pointerup", this.boundPointerUpHandler);
    canvas.removeEventListener("pointercancel", this.boundPointerCancelHandler);
    this.pointerHandlersAttached = false;
  }

  handlePointerDown(event) {
    if (event.button !== undefined && event.button !== 0) {
      return;
    }

    this.setDrawingFocus(true);
    this.renderer.canvas?.setPointerCapture?.(event.pointerId);
    this.dispatchPointerEvent("onPointerDown", event);
  }

  handlePointerMove(event) {
    this.dispatchPointerEvent("onPointerMove", event);
  }

  handlePointerUp(event) {
    this.dispatchPointerEvent("onPointerUp", event);
    this.renderer.canvas?.releasePointerCapture?.(event.pointerId);
  }

  handlePointerCancel(event) {
    this.toolRegistry.getActive()?.cancel();
    this.clearPreview();
    this.renderer.canvas?.releasePointerCapture?.(event.pointerId);
  }

  dispatchPointerEvent(methodName, event) {
    const tool = this.toolRegistry.getActive();
    const handler = tool?.[methodName];

    if (typeof handler !== "function") {
      return;
    }

    event.preventDefault();
    handler.call(tool, this.createToolContext(event));
  }

  createToolContext(event) {
    const screenPoint = this.getCanvasPoint(event);
    const worldPoint = screenToWorld(screenPoint, this.viewport);

    return {
      event,
      screenPoint,
      worldPoint,
      config: this.config,
      viewport: this.getViewport(),
      store: this.store,
      dataManager: this.dataManager,
      getDocument: () => this.store.getDocument(),
      render: () => this.render(),
      setPreview: (preview) => this.setPreview(preview),
      clearPreview: () => this.clearPreview(),
      panBy: (delta) => this.panBy(delta),
      panTo: (point) => this.panTo(point),
      zoomBy: (factor, anchor) => this.zoomBy(factor, anchor),
      zoomTo: (zoom, anchor) => this.zoomTo(zoom, anchor),
      screenToWorld: (point) => screenToWorld(point, this.viewport),
      worldToScreen: (point) => worldToScreen(point, this.viewport)
    };
  }

  handleWheel(event) {
    event.preventDefault();
    this.setDrawingFocus(true);

    const direction = event.deltaY < 0 ? 1 : -1;
    const factor = direction > 0 ? this.config.viewport.zoomStep : 1 / this.config.viewport.zoomStep;
    this.zoomBy(factor, this.getCanvasPoint(event));
  }

  handleKeyDown(event) {
    if (!this.config.viewport.keyboard || !this.drawingFocused || isEditableElement(event.target) || isEditableElement(getActiveElement())) {
      return;
    }

    const panStep = this.config.viewport.panStep;
    const zoomStep = this.config.viewport.zoomStep;
    const handlers = {
      ArrowUp: () => this.panBy({ x: 0, y: panStep }),
      ArrowDown: () => this.panBy({ x: 0, y: -panStep }),
      ArrowLeft: () => this.panBy({ x: panStep, y: 0 }),
      ArrowRight: () => this.panBy({ x: -panStep, y: 0 }),
      "+": () => this.zoomBy(zoomStep),
      "=": () => this.zoomBy(zoomStep),
      "-": () => this.zoomBy(1 / zoomStep),
      "_": () => this.zoomBy(1 / zoomStep),
      "0": () => this.resetViewport()
    };
    const handler = handlers[event.key];

    if (!handler) {
      return;
    }

    event.preventDefault();
    handler();
  }

  getCanvasPoint(event) {
    const canvas = this.renderer.canvas;

    if (!canvas) {
      return this.getViewportCenter();
    }

    const rect = canvas.getBoundingClientRect();

    return {
      x: ((event.clientX - rect.left) / rect.width) * canvas.width,
      y: ((event.clientY - rect.top) / rect.height) * canvas.height
    };
  }

  getViewportCenter() {
    const canvas = this.renderer.canvas;

    return {
      x: (canvas?.width ?? this.config.canvas.width) / 2,
      y: (canvas?.height ?? this.config.canvas.height) / 2
    };
  }

  seedDemoElements() {
    const documentModel = this.store.getDocument();

    if (documentModel.dots.length > 0 || documentModel.objects.length > 0) {
      return;
    }

    this.dataManager.addDot(
      new Dot({
        id: "dot-1",
        x: 120,
        y: 120,
        meta: { radius: 8, color: "#cc5500" }
      })
    );

    this.dataManager.addDot(
      new Dot({
        id: "dot-2",
        x: 180,
        y: 160,
        meta: { radius: 4, color: "#0047ab" }
      })
    );

    this.dataManager.addDot(
      new Dot({
        id: "dot-3",
        x: 420,
        y: 240,
        meta: { radius: 4, color: "#0047ab" }
      })
    );

    this.dataManager.addDot(
      new Dot({
        id: "dot-4",
        x: 460,
        y: 100,
        meta: { radius: 4, color: "#0f5132" }
      })
    );

    this.dataManager.addDot(
      new Dot({
        id: "dot-5",
        x: 680,
        y: 100,
        meta: { radius: 4, color: "#0f5132" }
      })
    );

    this.dataManager.addDot(
      new Dot({
        id: "dot-6",
        x: 680,
        y: 240,
        meta: { radius: 4, color: "#0f5132" }
      })
    );

    this.dataManager.addDot(
      new Dot({
        id: "dot-7",
        x: 460,
        y: 240,
        meta: { radius: 4, color: "#0f5132" }
      })
    );

    this.dataManager.addObject(
      new LineObject({
        id: "line-1",
        points: ["dot-2", "dot-3"],
        strokeColor: "#0047ab",
        lineWidth: 3
      })
    );

    this.dataManager.addObject(
      new RectangleObject({
        id: "rectangle-1",
        points: ["dot-4", "dot-5", "dot-6", "dot-7"],
        strokeColor: "#0f5132",
        fillColor: "rgba(31, 122, 90, 0.16)",
        lineWidth: 2
      })
    );
  }
}

function areViewportsEqual(left, right) {
  return left.zoom === right.zoom
    && left.minZoom === right.minZoom
    && left.maxZoom === right.maxZoom
    && left.panX === right.panX
    && left.panY === right.panY
    && left.zoomStep === right.zoomStep
    && left.panStep === right.panStep
    && left.wheelZoom === right.wheelZoom
    && left.keyboard === right.keyboard;
}

function getActiveElement() {
  return typeof document === "undefined" ? null : document.activeElement;
}

function isEditableElement(element) {
  if (!element) {
    return false;
  }

  if (element.isContentEditable) {
    return true;
  }

  const tagName = element.tagName?.toLowerCase();

  if (tagName === "textarea" || tagName === "select") {
    return true;
  }

  return tagName === "input";
}

function resolveContainer(target) {
  if (typeof target === "string") {
    const element = document.querySelector(target);

    if (!element) {
      throw new Error(`Mount target not found: ${target}`);
    }

    return element;
  }

  if (!target) {
    throw new Error("A mount target is required.");
  }

  return target;
}
